"use client";

import { useEffect, useState } from "react";
import { AudioLines, RotateCcw } from "lucide-react";

// The tool itself is a separate, self-contained app — its own FastAPI +
// WebSocket backend (Split ONNX model + the real Enhance DLL under Wine),
// deployed independently and reverse-proxied by nginx at /neonoise/ (see
// deploy/nginx-neonoise.conf in the deploy kit). This page is the site's
// frame around it: our nav, our heading, our colors. The iframe's own
// index.html was recolored to the site's dark palette (see its <style>
// block) but keeps its real-time canvas UI and WebSocket wiring untouched —
// that part was verified end to end by the session that built the backend,
// and hand-porting it into React would only risk breaking what already
// works. /neonoise/ (trailing slash matters — the tool computes its own API
// base from location.pathname) must never be proxied through Next.js: it
// needs to reach its own backend directly.
const TOOL_SRC = "/neonoise/";

// If the iframe's own load fails outright (e.g. it catches the backend mid-
// restart during a deploy), the browser does not always fire anything an
// <iframe> can listen for — onError is unreliable across browsers for HTTP-
// level failures, so a hung load can otherwise sit on "Loading…" forever
// with no way out short of the user knowing to hard-refresh the whole page.
// This timeout is the actual fix for that: it does not depend on the
// failure announcing itself, and "reload" here remounts just the iframe
// (fresh request, fresh JS state) rather than needing a full page refresh.
const LOAD_TIMEOUT_MS = 12_000;

// Until the tool reports what it actually needs (see the message listener
// below), roughly what it settles on at full width with no video loaded.
const INITIAL_FRAME_HEIGHT = 640;

export function NeoNoisePage() {
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [frameHeight, setFrameHeight] = useState(INITIAL_FRAME_HEIGHT);

  // The tool sizes its panel from the frame's width and posts the height
  // that layout needs — taller once a video preview is showing, shorter on
  // a phone — so the frame hugs it instead of reserving a worst-case box
  // with a dead band under the transport. Same origin by construction
  // (/neonoise/ is served from this domain); anything else is ignored.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "neonoise:height" && typeof e.data.height === "number") {
        setFrameHeight(e.data.height);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    setTimedOut(false);
    if (loaded) return;
    const t = setTimeout(() => setTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
    // `attempt` is the retry trigger: bumping it re-arms this timeout for
    // the new iframe instance below, same as `loaded` resetting to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  function retry() {
    setLoaded(false);
    setTimedOut(false);
    setAttempt((n) => n + 1);
  }

  return (
    // The tool scales itself to whatever frame it is given (its own fit()
    // solves for the largest panel that fits the iframe), so this max-width
    // and the iframe height below ARE its size control: at 1700px x 900px it
    // grew to about twice its design size and took over the whole window
    // (owner, 2026-09-19). 5xl lands it near 1.4x; the height follows from
    // that width and is reported by the tool itself (frameHeight).
    <main className="mx-auto w-full max-w-5xl flex-1 space-y-8 px-4 py-10">
      <div className="mx-auto max-w-3xl space-y-3 text-center">
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Neo Noise</h1>
        <p className="leading-relaxed text-muted-foreground">
          Clean up a noisy recording right here. Upload audio or video and clean across the whole
          spectrum or band by band, restore the voice with Enhance, and download the result. A
          video comes back with its picture untouched and the cleaned sound in place.
        </p>
        <p className="text-sm text-muted-foreground/70">Files up to 5 minutes and 400 MB.</p>
      </div>

      <div className="mx-auto w-full">
        <div className="relative overflow-hidden rounded-xl border border-border/60 bg-card/40">
          {!loaded && !timedOut && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <AudioLines className="size-6 animate-pulse" />
              <p className="text-xs">Loading Neo Noise…</p>
            </div>
          )}
          {timedOut && !loaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <p className="text-xs">This is taking longer than expected.</p>
              <button
                type="button"
                onClick={retry}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-secondary px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary/70"
              >
                <RotateCcw className="size-3.5" />
                Try again
              </button>
            </div>
          )}
          <iframe
            key={attempt}
            src={TOOL_SRC}
            onLoad={() => setLoaded(true)}
            title="Neo Noise"
            className="block w-full border-0 bg-transparent transition-opacity duration-300"
            style={{ opacity: loaded ? 1 : 0, height: frameHeight }}
            allow="clipboard-write"
          />
        </div>
      </div>
    </main>
  );
}
